import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Divider, Descriptions, Spin, message } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_PRICING_SCHEME_DETAIL, type PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';
import type { DSPPricingFormData } from './dsp.types';

interface DSPPricingDetailProps {
    pricingSchemeId: number;
    onBack?: () => void;
    onEdit?: (schemeId: number) => void;
}

const DSPPricingDetail: React.FC<DSPPricingDetailProps> = ({
    pricingSchemeId,
    onBack,
    onEdit
}) => {
    // 表单数据状态
    const [formData, setFormData] = useState<DSPPricingFormData & { 
        creator?: string; 
        createdAt?: string; 
        updatedAt?: string;
        operator?: string;
        bindCount?: number;
    }>({
        pricingSchemeName: '',
        warehouseId: null,
        warehouseName: '',
        line: '',
        pricingSchemeDescription: ''
    });

    // 获取定价方案详情
    const { data: schemeDetailData, loading: schemeDetailLoading } = useQuery<PricingSchemeDetailResponse>(
        GET_PRICING_SCHEME_DETAIL,
        {
            variables: { pricingSchemeId },
            fetchPolicy: 'network-only'
        }
    );

    // 加载详情数据
    useEffect(() => {
        const detailResult = schemeDetailData?.getPricingSchemeDetail;
        if (detailResult?.messageKey || detailResult?.message) {
            message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
        }
        if (schemeDetailData?.getPricingSchemeDetail) {
            const detail = schemeDetailData.getPricingSchemeDetail;

            // 从 priceConfig 中解析 DSP 特有字段
            let warehouseId: string | null = null;
            let warehouseName = '';
            let line = '';

            if (detail.priceConfig && detail.priceConfig.length > 0) {
                const firstConfig = detail.priceConfig[0];
                if (firstConfig.allPriceConfig && firstConfig.allPriceConfig.length > 0) {
                    try {
                        const contentConfigArray = JSON.parse(firstConfig.allPriceConfig[0].contentPriceConfig || '[]');
                        const contentConfig = Array.isArray(contentConfigArray) ? contentConfigArray[0] : contentConfigArray;
                        if (contentConfig) {
                            warehouseId = contentConfig.warehouseId?.toString() || null;
                            warehouseName = contentConfig.warehouseName || '';
                            line = contentConfig.dspLine || contentConfig.line || '';
                        }
                    } catch (e) {
                        console.error('解析 DSP 配置失败:', e);
                    }
                }
            }

            setFormData({
                pricingSchemeName: detail.pricingSchemeName || '',
                warehouseId: warehouseId,
                warehouseName: warehouseName,
                line: line,
                pricingSchemeDescription: detail.pricingSchemeDescription || '',
                bindCount: detail.bindCount
            });
        }
    }, [schemeDetailData]);

    if (schemeDetailLoading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>加载中...</div>
            </div>
        );
    }

    return (
        <div>
            <Descriptions 
                title="DSP定价方案详情" 
                bordered 
                column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                labelStyle={{ width: '150px', fontWeight: 500 }}
            >
                <Descriptions.Item label="方案ID">
                    {pricingSchemeId}
                </Descriptions.Item>
                <Descriptions.Item label="方案名称">
                    {formData.pricingSchemeName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="仓库">
                    {formData.warehouseName || formData.warehouseId || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="线路">
                    {formData.line || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="绑定数量">
                    {formData.bindCount ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>
                    {formData.pricingSchemeDescription || '-'}
                </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Row justify="end" style={{ margin: 25 }}>
                <Col>
                    <Button type="default" size="large" style={{ margin: 15 }} onClick={onBack}>
                        返回
                    </Button>
                    <Button 
                        type="primary" 
                        size="large" 
                        style={{ margin: 15 }} 
                        onClick={() => onEdit && onEdit(pricingSchemeId)}
                    >
                        编辑
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default DSPPricingDetail;
